import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import SEOHead from './SEOHead';
import { 
  Crown, 
  CheckCircle, 
  ArrowRight,
  Target,
  Eye,
  Heart,
  Users,
  Award,
  Shield,
  Phone,
  Clock,
  Zap,
  Star,
  Building,
  Handshake
} from 'lucide-react';

const AboutPage = () => {
  // Company Entity Information - Single Source of Truth
  const companyInfo = {
    name: 'King of IT Inc.',
    founded: '31 years ago',
    headquarters: 'Toronto, Ontario, Canada',
    serviceArea: 'Toronto & Greater Toronto Area',
    specialization: 'Managed IT Services, IT Support, Cybersecurity, Microsoft 365, Cloud & Network Services',
    primaryCustomers: 'Small and mid-sized businesses (10-200 employees)',
    phone: '647-951-3653',
    email: 'support@kingofit.ca',
    website: 'kingofit.ca',
    experienceYears: '31 years combined IT industry experience'
  };

  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To deliver exceptional IT solutions that empower businesses to thrive. We focus on doing it right – building long-term partnerships through quality service and honest advice.'
    },
    {
      icon: Eye,
      title: 'Our Vision',
      description: 'To become the premier IT provider of cutting-edge enterprise solutions in Canada, fostering business growth through strategic adoption of top-tier technologies aimed at cost reduction and performance optimization.'
    },
    {
      icon: Heart,
      title: 'Our Values',
      description: 'Integrity, excellence, and customer focus guide everything we do. We build lasting relationships by treating every client\'s business as if it were our own, delivering quality IT solutions with honesty and transparency.'
    }
  ];

  const whyChooseUs = [
    'Same-day response and 24/7 emergency support',
    'Customer-focused approach to every project',
    'Commitment to client satisfaction',
    'Microsoft, Cisco, and CompTIA certified professionals',
    'Tailored solutions for your specific needs',
    'Transparent pricing with no hidden costs',
    'Proactive maintenance and monitoring',
    'Long-term partnership mindset',
    '31 years combined IT industry experience',
    'Local team that knows the GTA market',
    'Vendor-neutral recommendations',
    'Comprehensive service portfolio'
  ];

  const timeline = [
    { year: 'Early 1990s', title: 'Company Founded', description: 'King of IT was established in Toronto to serve small and mid-sized businesses with quality managed IT services.' },
    { year: '2012', title: 'Expanded Services', description: 'Added managed services and cloud solutions to our portfolio, including Microsoft 365 migration services.' },
    { year: '2015', title: '100+ Clients', description: 'Reached milestone of serving over 100 satisfied business clients across the GTA.' },
    { year: '2018', title: 'Cybersecurity Focus', description: 'Expanded cybersecurity services to address growing digital threats facing Toronto businesses.' },
    { year: '2021', title: 'Remote Work Solutions', description: 'Helped hundreds of businesses transition to secure remote work during the pandemic.' },
    { year: '2024', title: '500+ Happy Clients', description: 'Celebrating 500+ satisfied clients across the Greater Toronto Area.' }
  ];

  const team = [
    {
      name: 'Expert Technicians',
      role: 'Certified IT Professionals',
      description: 'Our team holds certifications from Microsoft, Cisco, CompTIA, and more.'
    },
    {
      name: 'Dedicated Support',
      role: 'Help Desk Team',
      description: 'Friendly support staff available to help with any IT issue, big or small.'
    },
    {
      name: 'Strategic Advisors',
      role: 'IT Consultants',
      description: 'Experienced consultants who help plan your technology roadmap.'
    }
  ];

  return (
    <>
      <SEOHead
        title="About King of IT | Toronto Managed IT Services Company | 31 Years Experience | 647-951-3653"
        description="King of IT is a Toronto-based managed IT services company with 31 years of experience. We provide IT support, cybersecurity, Microsoft 365, and cloud services to 500+ small and mid-sized businesses across the GTA."
        path="/about"
      />
      
      <main role="main">
        {/* Company Entity Section - Structured for AI */}
        <section className="bg-gradient-navy text-white py-16 lg:py-20" aria-labelledby="about-hero-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">
                  About King of IT
                </Badge>
                <h1 id="about-hero-heading" className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mb-4 sm:mb-6">
                  Toronto <span className="text-gradient-gold">Managed IT Services</span> Company
                </h1>
                
                {/* Structured Company Profile for AI Retrieval */}
                <div className="bg-white/10 rounded-xl p-6 mb-6 backdrop-blur-sm">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <dt className="text-gray-400 text-xs uppercase tracking-wide">Company</dt>
                      <dd className="text-white font-medium">{companyInfo.name}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-400 text-xs uppercase tracking-wide">Founded</dt>
                      <dd className="text-white font-medium">{companyInfo.founded}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-400 text-xs uppercase tracking-wide">Headquarters</dt>
                      <dd className="text-white font-medium">{companyInfo.headquarters}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-400 text-xs uppercase tracking-wide">Service Area</dt>
                      <dd className="text-white font-medium">{companyInfo.serviceArea}</dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-gray-400 text-xs uppercase tracking-wide">Specialization</dt>
                      <dd className="text-white font-medium">{companyInfo.specialization}</dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-gray-400 text-xs uppercase tracking-wide">Primary Customers</dt>
                      <dd className="text-white font-medium">{companyInfo.primaryCustomers}</dd>
                    </div>
                  </dl>
                </div>

                <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6">
                  King of IT has 31 years of IT industry experience. We provide managed IT, cybersecurity, Microsoft 365, cloud, and network services to <strong>500+ small and mid-sized businesses</strong> throughout Toronto and the Greater Toronto Area.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact" data-testid="about-hero-cta">
                    <Button size="lg" className="w-full sm:w-auto bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90">
                      Contact Us Today
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <a href="tel:6479513653" data-testid="about-hero-phone">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10">
                      <Phone className="w-5 h-5 mr-2" />
                      647-951-3653
                    </Button>
                  </a>
                </div>
              </div>
              <div className="relative">
                <img
                  src="/images/optimized/pexels-3182812.webp"
                  alt="King of IT professional team providing IT support and consulting services in Toronto"
                  className="rounded-2xl shadow-2xl"
                  loading="eager"
                />
                <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 bg-gradient-gold rounded-xl p-4 sm:p-6 shadow-xl">
                  <div className="flex items-center gap-3">
                    <Crown className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                    <div className="text-white">
                      <p className="font-bold text-xl sm:text-2xl">31 Years</p>
                      <p className="text-xs sm:text-sm opacity-90">31 Years Experience</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What King of IT Does Section */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="space-y-6">
                <Badge className="bg-primary/10 text-primary border-primary/20">
                  What King of IT Does
                </Badge>
                <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground">
                  Managed IT Services for <span className="text-gradient-gold">Growing Businesses</span>
                </h2>
                <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                  King of IT provides comprehensive managed IT services designed for small and mid-sized businesses. 
                  We handle your technology so you can focus on running your business.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Managed IT Support</strong>
                      <p className="text-muted-foreground text-sm">Proactive monitoring, maintenance, and help desk support for your entire IT infrastructure.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Cybersecurity</strong>
                      <p className="text-muted-foreground text-sm">Endpoint protection, email security, MFA, threat monitoring, and security awareness training.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Microsoft 365</strong>
                      <p className="text-muted-foreground text-sm">Migration, configuration, licensing optimization, SharePoint, Teams, and ongoing administration.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Cloud & Network</strong>
                      <p className="text-muted-foreground text-sm">Cloud migration, network design, WiFi setup, VPN, and infrastructure management.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Backup & Disaster Recovery</strong>
                      <p className="text-muted-foreground text-sm">Automated backups, business continuity planning, and rapid recovery solutions.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src="/images/optimized/pexels-3184291.webp"
                  alt="IT Professional configuring business network and cloud services"
                  className="rounded-2xl shadow-xl"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Who We Serve Section */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-16">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                Who King of IT Serves
              </Badge>
              <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground">
                Industries & <span className="text-gradient-gold">Business Types</span>
              </h2>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                We specialize in IT support for small and mid-sized businesses across various industries in the Greater Toronto Area.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="p-6 text-center hover:shadow-card-hover transition-shadow">
                <Building className="w-10 h-10 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Professional Services</h3>
                <p className="text-muted-foreground text-sm">Law firms, accounting firms, consulting agencies, and financial services.</p>
              </Card>
              <Card className="p-6 text-center hover:shadow-card-hover transition-shadow">
                <Shield className="w-10 h-10 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Healthcare</h3>
                <p className="text-muted-foreground text-sm">Medical clinics, dental offices, pharmacies, and healthcare providers.</p>
              </Card>
              <Card className="p-6 text-center hover:shadow-card-hover transition-shadow">
                <Users className="w-10 h-10 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Non-Profits</h3>
                <p className="text-muted-foreground text-sm">Charities, associations, foundations, and community organizations.</p>
              </Card>
              <Card className="p-6 text-center hover:shadow-card-hover transition-shadow">
                <Zap className="w-10 h-10 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Manufacturing & Retail</h3>
                <p className="text-muted-foreground text-sm">Manufacturers, distributors, retailers, and e-commerce businesses.</p>
              </Card>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-16">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                Our Foundation
              </Badge>
              <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground">
                Mission, Vision & <span className="text-gradient-gold">Values</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {values.map((value) => (
                <Card key={value.title} className="p-6 sm:p-8 text-center hover:shadow-card-hover transition-shadow h-full">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-xl bg-gradient-gold flex items-center justify-center shadow-gold">
                    <value.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <h3 className="font-display font-semibold text-lg sm:text-xl text-foreground mb-3 sm:mb-4">
                    {value.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                    {value.description}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-16">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                Our Journey
              </Badge>
              <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground">
                Growing with <span className="text-gradient-gold">Our Clients</span>
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {timeline.map((item, index) => (
                <Card key={item.year} className="p-6 hover:shadow-card-hover transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-gold flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{item.year}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                      <p className="text-muted-foreground text-sm">{item.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                  Why Choose Us
                </Badge>
                <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground mb-4 sm:mb-6">
                  What Makes Us <span className="text-gradient-gold">Different</span>
                </h2>
                <p className="text-muted-foreground text-base sm:text-lg mb-6 sm:mb-8">
                  We engage with companies to provide IT consulting and support services 
                  that make a real difference. Here&apos;s why businesses across the GTA trust King of IT:
                </p>
                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                  {whyChooseUs.map((item) => (
                    <div key={item} className="flex items-start gap-2 sm:gap-3">
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground text-sm sm:text-base">{item}</span>
                    </div>
                  ))}
                </div>
                <Link to="/contact">
                  <Button className="bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90 mt-6 sm:mt-8">
                    Contact Us Today
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4 sm:gap-6">
                <Card className="p-4 sm:p-6 text-center">
                  <Users className="w-8 h-8 sm:w-10 sm:h-10 text-primary mx-auto mb-2 sm:mb-3" />
                  <p className="font-bold text-2xl sm:text-3xl text-foreground">500+</p>
                  <p className="text-muted-foreground text-sm">Happy Clients</p>
                </Card>
                <Card className="p-4 sm:p-6 text-center">
                  <Award className="w-8 h-8 sm:w-10 sm:h-10 text-primary mx-auto mb-2 sm:mb-3" />
                  <p className="font-bold text-2xl sm:text-3xl text-foreground">30+</p>
                  <p className="text-muted-foreground text-sm">Years Experience</p>
                </Card>
                <Card className="p-4 sm:p-6 text-center">
                  <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-primary mx-auto mb-2 sm:mb-3" />
                  <p className="font-bold text-2xl sm:text-3xl text-foreground">100%</p>
                  <p className="text-muted-foreground text-sm">Satisfaction</p>
                </Card>
                <Card className="p-4 sm:p-6 text-center">
                  <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-primary mx-auto mb-2 sm:mb-3" />
                  <p className="font-bold text-2xl sm:text-3xl text-foreground">24/7</p>
                  <p className="text-muted-foreground text-sm">Support</p>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-16">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                Our Team
              </Badge>
              <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground">
                Expert <span className="text-gradient-gold">Professionals</span>
              </h2>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                Our team of certified IT professionals brings decades of combined experience to every project.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {team.map((member, index) => (
                <Card key={index} className="p-6 sm:p-8 text-center hover:shadow-card-hover transition-shadow">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full bg-gradient-gold flex items-center justify-center">
                    <Handshake className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg sm:text-xl text-foreground mb-1">{member.name}</h3>
                  <p className="text-primary text-sm mb-3">{member.role}</p>
                  <p className="text-muted-foreground text-sm">{member.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-24 bg-gradient-navy text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Crown className="w-12 h-12 sm:w-16 sm:h-16 text-primary mx-auto mb-4 sm:mb-6" />
            <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl mb-4 sm:mb-6">
              Ready to Work With the <span className="text-gradient-gold">King of IT?</span>
            </h2>
            <p className="text-gray-300 text-base sm:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto">
              If you want serious improvements in your business&apos;s approach to IT, 
              contact King of IT today. We&apos;d love to learn about your business and how we can help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90"
                >
                  Get Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="tel:6479513653">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  647-951-3653
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default AboutPage;
